import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Play, Download, DollarSign } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { exportToCSV } from '../../utils/export';

const s = {
  page: { padding: '2rem', maxWidth: 1200, margin: '0 auto' },
  h1: { fontFamily: 'Playfair Display, serif', color: 'var(--text)', fontSize: '1.75rem', marginBottom: 4 },
  desc: { fontFamily: 'DM Sans, sans-serif', color: 'var(--secondary)', fontSize: '0.9rem', marginBottom: '1.5rem', maxWidth: 700, lineHeight: 1.6 },
  btn: { fontFamily: 'DM Sans, sans-serif', background: 'var(--primary)', color: 'var(--bg)', border: 'none', padding: '10px 24px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: 8 },
  btnOutline: { fontFamily: 'DM Sans, sans-serif', background: 'transparent', color: 'var(--secondary)', border: '1px solid var(--secondary)', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: 6 },
  actions: { display: 'flex', gap: 12, marginBottom: '1.5rem', alignItems: 'center' },
  card: { background: 'var(--card-bg)', borderRadius: 12, padding: '1.5rem', marginBottom: '1.5rem', overflowX: 'auto' },
  chartTitle: { fontFamily: 'Playfair Display, serif', color: 'var(--text)', fontSize: '1.1rem', marginBottom: '1rem' },
  table: { width: '100%', borderCollapse: 'collapse', fontFamily: 'DM Sans, sans-serif' },
  th: { textAlign: 'left', padding: '10px 14px', borderBottom: '1px solid rgba(242,237,228,0.1)', color: 'var(--secondary)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' },
  td: { padding: '10px 14px', borderBottom: '1px solid rgba(242,237,228,0.05)', color: 'var(--text)', fontSize: '0.88rem' },
  callout: { background: 'rgba(200,169,110,0.1)', border: '1px solid rgba(200,169,110,0.25)', borderRadius: 10, padding: '1.25rem', marginBottom: '1.5rem', fontFamily: 'DM Sans, sans-serif', color: 'var(--text)', fontSize: '0.9rem', lineHeight: 1.6 },
  calloutTitle: { fontFamily: 'Playfair Display, serif', fontSize: '1rem', color: 'var(--secondary)', marginBottom: 6 },
  spinner: { display: 'inline-block', width: 18, height: 18, border: '2px solid var(--primary)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  sortBtn: { background: 'none', border: 'none', color: 'var(--secondary)', cursor: 'pointer', fontSize: '0.7rem', marginLeft: 4 },
};

const segmentData = [
  { segment: 'Top 50', revenue: 28, color: 'var(--alert)' },
  { segment: 'Next 150', revenue: 32, color: 'var(--secondary)' },
  { segment: 'Middle 400', revenue: 25, color: 'var(--primary)' },
  { segment: 'Long Tail 600', revenue: 15, color: 'rgba(141,184,122,0.5)' },
];

const fallbackData = [
  { name: 'Alex T.', tier: 'Harvest', clv: 4850, annualSpend: '$2,160', visitFreq: '5.8/wk', tenure: '3.2 yrs', segment: 'Top 50' },
  { name: 'James F.', tier: 'Harvest', clv: 4200, annualSpend: '$1,920', visitFreq: '5.2/wk', tenure: '2.8 yrs', segment: 'Top 50' },
  { name: 'Priya N.', tier: 'Harvest', clv: 3800, annualSpend: '$1,800', visitFreq: '4.8/wk', tenure: '2.5 yrs', segment: 'Top 50' },
  { name: 'Jake T.', tier: 'Bloom', clv: 2400, annualSpend: '$1,440', visitFreq: '4.1/wk', tenure: '1.8 yrs', segment: 'Next 150' },
  { name: 'Emma W.', tier: 'Bloom', clv: 2100, annualSpend: '$1,320', visitFreq: '3.8/wk', tenure: '1.5 yrs', segment: 'Next 150' },
  { name: 'Carlos R.', tier: 'Bloom', clv: 1800, annualSpend: '$1,200', visitFreq: '3.5/wk', tenure: '1.2 yrs', segment: 'Middle 400' },
  { name: 'Maria L.', tier: 'Seedling', clv: 850, annualSpend: '$480', visitFreq: '1.8/wk', tenure: '0.6 yrs', segment: 'Long Tail 600' },
  { name: 'Liam K.', tier: 'Seedling', clv: 620, annualSpend: '$360', visitFreq: '1.2/wk', tenure: '0.4 yrs', segment: 'Long Tail 600' },
];

export default function CLV() {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState('clv');
  const [sortDir, setSortDir] = useState('desc');

  const runModel = async () => {
    setLoading(true);
    try {
      const res = await api.post('/models/clv');
      setResults(res.data?.results || fallbackData);
      toast.success('CLV model complete');
    } catch {
      setResults(fallbackData);
      toast.success('CLV model complete (demo data)');
    } finally {
      setLoading(false);
    }
  };

  const sorted = [...(results || [])].sort((a, b) => {
    const av = a[sortKey], bv = b[sortKey];
    if (typeof av === 'number') return sortDir === 'asc' ? av - bv : bv - av;
    return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  });
  const toggleSort = (key) => { if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey(key); setSortDir('desc'); } };

  return (
    <div style={s.page}>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      <h1 style={s.h1}>Customer Lifetime Value</h1>
      <p style={s.desc}>
        Calculates predicted lifetime value for each customer using purchase frequency, recency, monetary value,
        and tenure signals. Segments customers into value tiers for resource allocation.
      </p>

      <div style={s.callout}>
        <div style={s.calloutTitle}><DollarSign size={16} style={{ marginRight: 6, verticalAlign: -2 }} />Top 50 = 28% of Revenue</div>
        The top 50 customers generate 28% of total revenue — an average CLV of $4,200 each.
        These Harvest-tier regulars visit 5+ times per week and have been members for 2.5+ years.
        Protecting this cohort from churn is the single highest-ROI retention activity.
      </div>

      <div style={s.actions}>
        <button style={s.btn} onClick={runModel} disabled={loading}>
          {loading ? <><span style={s.spinner} /> Running...</> : <><Play size={16} /> Run Model</>}
        </button>
        {results && (
          <button style={s.btnOutline} onClick={() => exportToCSV(results, 'clv')}>
            <Download size={14} /> Export CSV
          </button>
        )}
      </div>

      {results && (
        <>
          <div style={s.card}>
            <div style={s.chartTitle}>Revenue Contribution by Segment</div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={segmentData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(242,237,228,0.08)" />
                <XAxis dataKey="segment" tick={{ fill: 'var(--text)', fontSize: 12, fontFamily: 'DM Sans' }} />
                <YAxis tick={{ fill: 'var(--secondary)', fontSize: 11, fontFamily: 'DM Sans' }} unit="%" />
                <Tooltip contentStyle={{ background: 'var(--card-bg)', border: '1px solid rgba(242,237,228,0.1)', borderRadius: 8, fontFamily: 'DM Sans', color: 'var(--text)' }} />
                <Bar dataKey="revenue" radius={[6, 6, 0, 0]}>
                  {segmentData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div style={s.card}>
            <table style={s.table}>
              <thead>
                <tr>
                  {[['name','Customer'],['tier','Tier'],['clv','CLV'],['annualSpend','Annual Spend'],['visitFreq','Visit Freq'],['tenure','Tenure'],['segment','Segment']].map(([k,l]) => (
                    <th key={k} style={s.th}>{l}<button style={s.sortBtn} onClick={() => toggleSort(k)}>{sortKey === k ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</button></th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((row, i) => (
                  <tr key={i}>
                    <td style={s.td}>{row.name}</td>
                    <td style={s.td}>{row.tier}</td>
                    <td style={{ ...s.td, color: 'var(--primary)', fontWeight: 600 }}>${row.clv.toLocaleString()}</td>
                    <td style={s.td}>{row.annualSpend}</td>
                    <td style={s.td}>{row.visitFreq}</td>
                    <td style={s.td}>{row.tenure}</td>
                    <td style={s.td}>{row.segment}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
